import { cva, type VariantProps } from "class-variance-authority"
import type * as React from "react"
import { cn } from "../../lib/utils"
import { Icon } from "../Icon/Icon"
import type { IconName } from "../Icon/types"
import { IconButton } from "../IconButton/IconButton"

const tagVariants = cva(
  "inline-flex items-center justify-center gap-1 rounded-100 text-16 font-500 leading-1 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-0",
  {
    variants: {
      variant: {
        primary: "bg-feedback-primary-background text-root-inverse-text-base",
        secondary: "bg-feedback-secondary-background text-feedback-base-text",
        tertiary: "bg-feedback-tertiary-background text-feedback-base-text",
        danger: "bg-feedback-danger-background text-feedback-base-text",
      },
      size: {
        md: "py-1",
        sm: "py-0",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
)

const getTagPadding = (
  hasIcon: boolean,
  hasClose: boolean,
  size: "md" | "sm" | null | undefined
): string => {
  const leftPadding = hasIcon ? "pl-2" : size === "sm" ? "pl-3" : "pl-4"
  const rightPadding = hasClose ? "pr-1" : size === "sm" ? "pr-3" : "pr-4"

  return `${leftPadding} ${rightPadding}`
}

const getCloseButtonVariant = (
  tagVariant: "primary" | "secondary" | "tertiary" | "danger" | null | undefined
) => {
  return tagVariant === "primary" ? "ghost-inverse" : "ghost"
}

export interface TagProps
  extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    keyof VariantProps<typeof tagVariants> | "onClick"
  > {
  variant?: VariantProps<typeof tagVariants>["variant"]
  size?: VariantProps<typeof tagVariants>["size"]
  iconPrefix?: IconName
  onClose?: () => void
}
function Tag({
  className,
  variant = "primary",
  size = "md",
  children,
  iconPrefix,
  onClose,
  ...props
}: TagProps) {
  const paddingClasses = getTagPadding(!!iconPrefix, !!onClose, size)

  return (
    <div {...props} className={cn(tagVariants({ variant, size }), paddingClasses, className)}>
      {iconPrefix && <Icon name={iconPrefix} />}
      <span>{children}</span>
      {onClose && (
        <IconButton
          variant={getCloseButtonVariant(variant)}
          size="sm"
          icon="Close"
          onClick={onClose}
          aria-label={`Remove ${children}`}
        />
      )}
    </div>
  )
}

export { Tag, tagVariants }

Tag.displayName = "Tag"
